import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import DismissRoutes from "@/components/DismissRoutes";

// The admin pages are lazy chunks (they carry recharts and 40+ screens no
// visitor needs). Each loader is also kept here so AdminChunkWarmer can
// prefetch the whole set once an admin is around: after that, page changes
// are as instant as before the split (import() dedupes, so warming and the
// lazy components share one fetch).
const adminImports = [];
const adminLazy = (loader) => { adminImports.push(loader); return lazy(loader); };

// a swipe-delete the last page was still holding (its Undo window open at a
// refresh or an app kill) is carried out before any list asks the server
replayPendingDeletes();

let adminWarmed = false;
function AdminChunkWarmer() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (adminWarmed) return;
    const isAdminHere = pathname === "/admin" || pathname.startsWith("/admin/");
    let hasAdminSession = false;
    try { hasAdminSession = !!localStorage.getItem("adminToken"); } catch { /* storage blocked */ }
    if (!isAdminHere && !hasAdminSession) return;
    adminWarmed = true;
    // after the current page has settled; failures just mean a later
    // navigation fetches normally
    const t = setTimeout(() => adminImports.forEach((load) => { try { load().catch(() => {}); } catch { /* ignore */ } }), 1500);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

// In-app navigation to a lazy page never shows this - DismissRoutes swaps
// routes in a transition, so the old page holds until the chunk is in - but
// a COLD load of a lazy route does, so it fills the viewport with the
// theme's own background (body.dark keeps it dark) instead of flashing white.
const RouteFallback = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "var(--ion-background-color, #ffffff)" }}>
    <div style={{ width: 28, height: 28, border: "3px solid rgba(128,128,128,0.25)", borderTopColor: "#34d399", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
  </div>
);
import { HelmetProvider } from "react-helmet-async";
import Home from "@/pages/Home";
import PaystubSamples from "@/pages/PaystubSamples";
import PaystubGeneratorHub from "@/pages/PaystubGeneratorHub";
import StatePaystubLanding from "@/pages/StatePaystubLanding";
import MintSlipVsThePayStubs from "@/pages/MintSlipVsThePayStubs";
import CeaseAndDesistLanding from "@/pages/CeaseAndDesistLanding";
import PowerOfAttorneyLanding from "@/pages/PowerOfAttorneyLanding";
import CommercialLeaseLanding from "@/pages/CommercialLeaseLanding";
import HowToMakePaystub from "@/pages/HowToMakePaystub";
import PaystubForApartment from "@/pages/PaystubForApartment";
import FAQ from "@/pages/FAQ";
import About from "@/pages/About";
import NotFound from "@/pages/NotFound";
const AdminAnalytics = adminLazy(() => import("@/pages/AdminAnalytics"));
const AdminDiscounts = adminLazy(() => import("@/pages/AdminDiscounts"));
const AdminBlog = adminLazy(() => import("@/pages/AdminBlog"));
const AdminBlogEditor = adminLazy(() => import("@/pages/AdminBlogEditor"));
const AdminBannedIPs = adminLazy(() => import("@/pages/AdminBannedIPs"));
const AdminEmailTemplates = adminLazy(() => import("@/pages/AdminEmailTemplates"));
const AdminTemplates = adminLazy(() => import("@/pages/AdminTemplates"));
const AdminTemplateEditor = adminLazy(() => import("@/pages/AdminTemplateEditor"));
const AdminTaxLayouts = adminLazy(() => import("@/pages/AdminTaxLayouts"));
import SignRequest from "@/pages/SignRequest";
import AppSignatures from "@/pages/app/AppSignatures";
import AppSignDocEditor from "@/pages/app/AppSignDocEditor";
import AppTemplates from "@/pages/app/AppTemplates";
const AdminMassEmail = adminLazy(() => import("@/pages/AdminMassEmail"));
const AdminBroadcast = adminLazy(() => import("@/pages/AdminBroadcast"));
const AdminModerators = adminLazy(() => import("@/pages/AdminModerators"));
const AdminModeratorDetail = adminLazy(() => import("@/pages/AdminModeratorDetail"));
const AdminAuditLog = adminLazy(() => import("@/pages/AdminAuditLog"));
const AdminSupport = adminLazy(() => import("@/pages/AdminSupport"));
const AdminLiveChat = adminLazy(() => import("@/pages/AdminLiveChat"));
const AdminExport = adminLazy(() => import("@/pages/AdminExport"));
const AdminRevenue = adminLazy(() => import("@/pages/AdminRevenue"));
const AdminSettings = adminLazy(() => import("@/pages/AdminSettings"));
const AdminSiteSettings = adminLazy(() => import("@/pages/AdminSiteSettings"));
const AdminSubscriptions = adminLazy(() => import("@/pages/AdminSubscriptions"));
const AdminPreferences = adminLazy(() => import("@/pages/AdminPreferences"));
const AdminPurchases = adminLazy(() => import("@/pages/AdminPurchases"));
const AdminUsers = adminLazy(() => import("@/pages/AdminUsers"));
const AdminUserDetail = adminLazy(() => import("@/pages/AdminUserDetail"));
const AdminSavedDocs = adminLazy(() => import("@/pages/AdminSavedDocs"));
const AdminCalendar = adminLazy(() => import("@/pages/AdminCalendar"));
import Banned from "@/pages/Banned";
import UserLogin from "@/pages/UserLogin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import ScrollToTop from "@/components/ScrollToTop";
import IonToaster from "@/components/IonToaster";
import UpdateNotifier from "@/components/UpdateNotifier";
import Reviews from "@/pages/Reviews";
import ComparisonPage from "@/pages/ComparisonPage";
import DocumentDirectory from "@/pages/DocumentDirectory";
import AIResumeLanding from "@/pages/AIResumeLanding";
import AppPaystub from "@/pages/app/AppPaystub";
import AppHome from "@/pages/app/AppHome";
import AppCanadianPaystub from "@/pages/app/AppCanadianPaystub";
import AppUkPayslip from "@/pages/app/AppUkPayslip";
import AppTaxForms from "@/pages/app/AppTaxForms";
import AppLegalForms from "@/pages/app/AppLegalForms";
import AppBusinessForms from "@/pages/app/AppBusinessForms";
import AppResumes from "@/pages/app/AppResumes";
import AppCustomDocs from "@/pages/app/AppCustomDocs";
import AppTerms from "@/pages/app/AppTerms";
import AppPrivacy from "@/pages/app/AppPrivacy";
import AppSettings from "@/pages/app/AppSettings";
import AppAccount from "@/pages/app/AppAccount";
import AppMyDocuments from "@/pages/app/AppMyDocuments";
import AppBilling from "@/pages/app/AppBilling";
import AppReferrals from "@/pages/app/AppReferrals";
import AppPreferences from "@/pages/app/AppPreferences";
import AppBusiness from "@/pages/app/AppBusiness";
import AppEmployees from "@/pages/app/AppEmployees";
const AdminHealth = adminLazy(() => import("@/pages/AdminHealth"));
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useState, useEffect } from "react";
import { trackPageView } from "@/utils/trackPageView";
import { replayPendingDeletes } from "@/utils/undoDelete";
import MaintenancePage from "@/pages/MaintenancePage";
import { MinimizedChatsProvider } from "@/contexts/MinimizedChatsContext";
import MinimizedChatsFAB from "@/components/MinimizedChatsFAB";
import SupportChatWidget from "@/components/SupportChatWidget";
import { syncStatusBar } from "@/utils/statusBar";
import { isNative } from "@/utils/nativeHttp";
import { consumePendingPushUrl } from "@/utils/nativePush";
import { appThemeDark } from "@/utils/appTheme";

// Stripe Configuration - Use environment variable
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_51SOOSM0OuJwef38xP0FqCJ3b45STthDKnJWP572LoODAaxGIq8ujrAwp1W0MeGkI6XczeweTr7lOLIKC6MnLadoX00iDo2VzYM";
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
// Loaded inside the Stripe card-element iframes so their text matches the app font
const STRIPE_ELEMENT_FONTS = {
  fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;700&display=swap" }],
};
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Messages API for minimized chats — swap out with real endpoints when ready
const messagesApi = {
  conversation: async (id) => {
    const token = localStorage.getItem("userToken") || localStorage.getItem("adminToken");
    const res = await fetch(`${BACKEND_URL}/api/messages/conversation/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return { messages: [] };
    return res.json();
  },
  send: async (loadId, recipientId, body) => {
    const token = localStorage.getItem("userToken") || localStorage.getItem("adminToken");
    const res = await fetch(`${BACKEND_URL}/api/messages/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ loadId, recipientId, body }),
    });
    if (!res.ok) throw new Error("Send failed");
    return res.json();
  },
};

// Resolve current user from localStorage (admin or regular user)
function useCurrentUser() {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const u = localStorage.getItem("userInfo");
      const a = localStorage.getItem("adminInfo");
      return u ? JSON.parse(u) : a ? JSON.parse(a) : null;
    } catch { return null; }
  });
  useEffect(() => {
    const sync = () => {
      try {
        const u = localStorage.getItem("userInfo");
        const a = localStorage.getItem("adminInfo");
        setCurrentUser(u ? JSON.parse(u) : a ? JSON.parse(a) : null);
      } catch {}
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  return currentUser;
}

// Maintenance Mode Check Wrapper
function MaintenanceCheck({ children }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/maintenance-status`);
        if (response.ok) {
          const data = await response.json();
          setIsMaintenanceMode(data.maintenance?.isActive || false);
        }
      } catch (error) {
        console.error("Error checking maintenance status:", error);
      } finally {
        setIsChecking(false);
      }
    };
    
    checkMaintenance();
  }, []);

  // Don't block while checking
  if (isChecking) {
    return children;
  }

  // Allow admin routes even during maintenance
  const isAdminRoute = window.location.pathname.startsWith("/admin");
  
  // Show maintenance page if in maintenance mode (except for admin routes)
  if (isMaintenanceMode && !isAdminRoute) {
    return <MaintenancePage />;
  }

  return children;
}

// IP Ban Check Wrapper — bans only guard the /app area, and the ban screen
// renders as an overlay INSIDE AppLayout (so its scrim blurs the live app,
// paywall-style). This wrapper is now a passthrough kept for structure.
function IPBanCheck({ children }) {
  return children;
}

const IS_MOBILE_APP = process.env.REACT_APP_IS_MOBILE === "true";

// The admin area, one list for both shells: the web app serves it as always,
// and the native app carries it too, so a signed-in admin can run the
// dashboard from their phone (admin pushes deep-link into these pages).
const adminRoutes = (
  <>
    {/* the admin signs in through the one /login page */}
    <Route path="/admin/login" element={<Navigate to="/login" replace />} />
    {/* bare /admin lands on the calendar, never the 404 */}
    <Route path="/admin" element={<Navigate to="/admin/calendar" replace />} />
    <Route path="/admin/calendar" element={<AdminCalendar />} />
    <Route path="/admin/overview" element={<AdminCalendar />} />
    {/* Dashboard removed — redirect old links to the calendar overview */}
    <Route path="/admin/dashboard" element={<Navigate to="/admin/calendar" replace />} />
    <Route path="/admin/purchases" element={<AdminPurchases />} />
    <Route path="/admin/users" element={<AdminUsers />} />
    <Route path="/admin/users/:id" element={<AdminUserDetail />} />
    <Route path="/admin/saved-docs" element={<AdminSavedDocs />} />
    <Route path="/admin/analytics" element={<AdminAnalytics />} />
    <Route path="/admin/discounts" element={<AdminDiscounts />} />
    <Route path="/admin/banned-ips" element={<AdminBannedIPs />} />
    <Route path="/admin/blog" element={<AdminBlog />} />
    <Route path="/admin/blog/new" element={<AdminBlogEditor />} />
    <Route path="/admin/blog/edit/:id" element={<AdminBlogEditor />} />
    <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
    <Route path="/admin/templates" element={<AdminTemplates />} />
    <Route path="/admin/templates/edit/:id" element={<AdminTemplateEditor />} />
    <Route path="/admin/tax-layouts" element={<AdminTaxLayouts />} />
    <Route path="/admin/mass-email" element={<AdminMassEmail />} />
    <Route path="/admin/broadcast" element={<AdminBroadcast />} />
    <Route path="/admin/moderators" element={<AdminModerators />} />
    <Route path="/admin/moderators/:id" element={<AdminModeratorDetail />} />
    <Route path="/admin/audit-log" element={<AdminAuditLog />} />
    <Route path="/admin/support" element={<AdminLiveChat />} />
    <Route path="/admin/export" element={<AdminExport />} />
    <Route path="/admin/revenue" element={<AdminRevenue />} />
    <Route path="/admin/health" element={<AdminHealth />} />
    <Route path="/admin/settings" element={<AdminSettings />} />
    <Route path="/admin/site-settings" element={<AdminSiteSettings />} />
    <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
    <Route path="/admin/preferences" element={<AdminPreferences />} />
  </>
);

function MobileApp() {
  return (
    <HelmetProvider>
      <Elements stripe={stripePromise} options={STRIPE_ELEMENT_FONTS}>
        <div className="App">
          <IonToaster />
          <UpdateNotifier />
          <BrowserRouter>
            <BodyThemeSync />
            <NativeAppLock />
            <PushTapNavigator />
            <AdminChunkWarmer />
            <Suspense fallback={<RouteFallback />}><DismissRoutes>
              <Route path="/app" element={<AppHome />} />
              <Route path="/app/paystubs" element={<AppPaystub />} />
              <Route path="/app/paystub" element={<AppPaystub />} />
              <Route path="/app/canadian-paystub" element={<AppCanadianPaystub />} />
              <Route path="/app/uk-payslips" element={<AppUkPayslip />} />
              <Route path="/app/tax-forms" element={<AppTaxForms />} />
              <Route path="/app/legal-forms" element={<AppLegalForms />} />
              <Route path="/app/business-forms" element={<AppBusinessForms />} />
              <Route path="/app/resumes" element={<AppResumes />} />
            <Route path="/app/c/:slug" element={<AppCustomDocs />} />
              <Route path="/app/terms" element={<AppTerms />} />
              <Route path="/app/privacy" element={<AppPrivacy />} />
              <Route path="/app/settings" element={<AppSettings />} />
              <Route path="/app/account" element={<AppAccount />} />
              <Route path="/app/my-documents" element={<AppMyDocuments />} />
              <Route path="/app/billing" element={<AppBilling />} />
              <Route path="/app/referrals" element={<AppReferrals />} />
            <Route path="/app/preferences" element={<AppPreferences />} />
            <Route path="/app/business" element={<AppBusiness />} />
            <Route path="/app/employees" element={<AppEmployees />} />
            <Route path="/app/signatures" element={<AppSignatures />} />
            <Route path="/app/signatures/documents/:id" element={<AppSignDocEditor />} />
            <Route path="/app/templates" element={<AppTemplates />} />
            <Route path="/app/templates/edit/:id" element={<AdminTemplateEditor />} />
            <Route path="/app/branding" element={<Navigate to="/app/preferences?segment=branding" replace />} />
              <Route path="/app/minty" element={<Navigate to="/app" replace />} />
              {/* Embedded card checkout lands here after paying in-app */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/sign/:token" element={<SignRequest />} />
              {/* The sidebar's Log in link: the same whodat sign-in page */}
              <Route path="/login" element={<UserLogin />} />
              {/* A signed-in admin's phone runs the dashboard too; NativeAppLock
                  bounces everyone else off /admin */}
              {adminRoutes}
              <Route path="*" element={<AppHome />} />
            </DismissRoutes></Suspense>
          </BrowserRouter>
        </div>
      </Elements>
    </HelmetProvider>
  );
}

// Dark mode belongs to /app and /admin only — their layouts set body.dark but
// unmount without removing it, which used to leave the marketing site dark.
// This re-syncs the body class from the right store on every navigation.
// The native app is the product only: whatever route it lands on (a stray
// deep link, a stale bundle, a marketing path), anything outside /app, the
// sign-in, the signing pages and the checkout return bounces to /app.
function NativeAppLock() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isNative) return;
    // A signed-in admin or moderator may use the admin area from the app
    // (admin pushes deep-link there); everyone else stays inside /app.
    const adminOk = (pathname === "/admin" || pathname.startsWith("/admin/")) && !!localStorage.getItem("adminToken");
    const ok = pathname === "/app" || pathname.startsWith("/app/")
      || pathname === "/login" || pathname.startsWith("/sign/")
      || pathname === "/payment-success" || adminOk;
    if (!ok) navigate("/app", { replace: true });
  }, [pathname, navigate]);
  return null;
}

// A tapped push can carry an in-app destination (data.url). This lives here,
// not in a layout, so the tap lands wherever the person is - /app or /admin -
// and the path parked by a cold-start tap is collected as soon as React is up.
function PushTapNavigator() {
  const navigate = useNavigate();
  useEffect(() => {
    const open = () => { const p = consumePendingPushUrl(); if (p) navigate(p); };
    open();
    window.addEventListener("push-open-url", open);
    return () => window.removeEventListener("push-open-url", open);
  }, [navigate]);
  return null;
}

function BodyThemeSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname === "/app" || pathname.startsWith("/app/")) {
      document.body.classList.toggle("dark", appThemeDark());
    } else if (pathname === "/admin" || pathname.startsWith("/admin/")) {
      document.body.classList.toggle("dark", localStorage.getItem("adminDarkMode") === "true");
    } else {
      document.body.classList.remove("dark");
    }
    syncStatusBar(); // the phone status bar icons follow the page theme
  }, [pathname]);
  // React is up: fade the native boot splash away (no-op on the web)
  useEffect(() => {
    const el = document.getElementById("boot-splash");
    if (!el) return;
    requestAnimationFrame(() => {
      el.classList.add("gone");
      setTimeout(() => { try { el.remove(); } catch { /* already gone */ } }, 400);
    });
  }, []);
  return null;
}

// First-party analytics ping on every route change (skips /admin itself)
function TrackPageViews() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  // a referral link (?ref=CODE) on any page: kept until the sign-in that
  // creates an account claims it (UserLogin), then forgotten
  useEffect(() => {
    try {
      const code = new URLSearchParams(window.location.search).get("ref");
      if (code && /^[A-Za-z0-9]{4,16}$/.test(code)) localStorage.setItem("mintslipRef", JSON.stringify({ code: code.toUpperCase(), at: Date.now() }));
    } catch { /* storage off */ }
  }, [pathname]);
  return null;
}

// The /app experience renders its own SupportChatWidget in AppLayout, so
// suppress the site-wide one on /app routes to avoid two chat buttons. The
// sign-in page has no bubble at all, and neither does the recipient-facing
// signing page.
function GlobalSupportWidget({ currentUser }) {
  const { pathname } = useLocation();
  if (pathname === "/app" || pathname.startsWith("/app/") || pathname === "/login") return null;
  if (pathname === "/sign" || pathname.startsWith("/sign/")) return null;
  return <SupportChatWidget currentUser={currentUser} />;
}

function App() {
  if (IS_MOBILE_APP) return <MobileApp />;
  const currentUser = useCurrentUser();

  return (
    <HelmetProvider>
      <Elements stripe={stripePromise} options={STRIPE_ELEMENT_FONTS}>
        <MaintenanceCheck>
          <IPBanCheck>
            <div className="App">
              <IonToaster />
              <UpdateNotifier />
              <BrowserRouter>
                <MinimizedChatsProvider currentUser={currentUser} messagesApi={messagesApi}>
                <MinimizedChatsFAB currentUser={currentUser} />
                <GlobalSupportWidget currentUser={currentUser} />
                {/* Discount banners show only inside /app — the AppLayout
                    mounts its own PromoBanner copy there. */}
                <ScrollToTop />
                <TrackPageViews />
                <BodyThemeSync />
                <NativeAppLock />
                <PushTapNavigator />
            <AdminChunkWarmer />
                {/* Routes wrapper: sidebar-hiding pages play the modal
                    dismiss animation before the route swaps */}
                <Suspense fallback={<RouteFallback />}><DismissRoutes>
                  <Route path="/" element={<Home />} />
              <Route path="/paystub-generator" element={<PaystubGeneratorHub />} />
              <Route path="/paystub-samples" element={<PaystubSamples />} />
              <Route path="/instant-paystub-generator" element={<Navigate to="/app/paystubs" replace />} />
              {/* State-specific paystub generator pages with SEO content */}
              <Route path="/paystub-generator/:state" element={<StatePaystubLanding />} />
              <Route path="/paystub-for-mortgage" element={<Navigate to="/app/paystubs" replace />} />
              <Route path="/paystub-template-download" element={<Navigate to="/app/paystubs" replace />} />
              <Route path="/create-a-paystub" element={<Navigate to="/app/paystubs" replace />} />
              <Route path="/self-employed-paystub-generator" element={<Navigate to="/app/paystubs" replace />} />
            <Route path="/contractor-paystub-generator" element={<Navigate to="/app/paystubs" replace />} />
            <Route path="/canadian-paystub-generator" element={<Navigate to="/app/canadian-paystub" replace />} />
            <Route path="/uk-payslip-generator" element={<Navigate to="/app/uk-payslips" replace />} />
            <Route path="/accounting-mockup-generator" element={<Navigate to="/app/business-forms" replace />} />
            <Route path="/w2-generator" element={<Navigate to="/app/tax-forms" replace />} />
            <Route path="/w9-generator" element={<Navigate to="/app/tax-forms" replace />} />
            <Route path="/1099-nec-generator" element={<Navigate to="/app/tax-forms" replace />} />
            <Route path="/1099-misc-generator" element={<Navigate to="/app/tax-forms" replace />} />
            <Route path="/offer-letter-generator" element={<Navigate to="/app/business-forms" replace />} />
            <Route path="/cease-and-desist-generator" element={<Navigate to="/app/legal-forms" replace />} />
            <Route path="/legal-document-generator" element={<Navigate to="/app/legal-forms" replace />} />
            {/* SEO landing pages for the cease and desist letter */}
            <Route path="/cease-and-desist-letter" element={<CeaseAndDesistLanding />} />
            <Route path="/cease-and-desist" element={<CeaseAndDesistLanding />} />
            <Route path="/cease-and-desist-template" element={<CeaseAndDesistLanding />} />
            <Route path="/power-of-attorney-generator" element={<Navigate to="/app/legal-forms" replace />} />
            {/* SEO landing pages for the durable power of attorney */}
            <Route path="/durable-power-of-attorney" element={<PowerOfAttorneyLanding />} />
            <Route path="/power-of-attorney" element={<PowerOfAttorneyLanding />} />
            <Route path="/power-of-attorney-form" element={<PowerOfAttorneyLanding />} />
            <Route path="/commercial-lease-generator" element={<Navigate to="/app/business-forms" replace />} />
            {/* SEO landing pages for the commercial lease */}
            <Route path="/commercial-lease-agreement" element={<CommercialLeaseLanding />} />
            <Route path="/commercial-lease" element={<CommercialLeaseLanding />} />
            <Route path="/commercial-lease-template" element={<CommercialLeaseLanding />} />
            <Route path="/schedule-c-generator" element={<Navigate to="/app/tax-forms" replace />} />
            <Route path="/vehicle-bill-of-sale-generator" element={<Navigate to="/app/legal-forms" replace />} />
            <Route path="/service-expense-generator" element={<Navigate to="/app/business-forms" replace />} />
            <Route path="/ai-resume-builder" element={<Navigate to="/app/resumes" replace />} />
            <Route path="/resume-builder" element={<AIResumeLanding />} />
            <Route path="/free-resume-builder" element={<AIResumeLanding />} />
            <Route path="/ai-resume-generator" element={<AIResumeLanding />} />
            <Route path="/how-to-make-a-paystub" element={<HowToMakePaystub />} />
            <Route path="/paystub-for-apartment" element={<PaystubForApartment />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Navigate to="/app/privacy" replace />} />
            <Route path="/terms" element={<Navigate to="/app/terms" replace />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/sign/:token" element={<SignRequest />} />
            {/* Blog Routes */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Admin Routes (shared with the native shell below) */}
            {adminRoutes}
            {/* Banned Page */}
            <Route path="/banned" element={<Banned />} />
            {/* Sign-in (email code). Accounts are created on the first sign-in;
                everything account-related lives inside /app. */}
            <Route path="/login" element={<UserLogin />} />
            {/* /app is the main app link; /app/paystub kept as an alias */}
            <Route path="/app" element={<AppHome />} />
            <Route path="/app/paystubs" element={<AppPaystub />} />
            <Route path="/app/paystub" element={<AppPaystub />} />
            <Route path="/app/canadian-paystub" element={<AppCanadianPaystub />} />
            <Route path="/app/uk-payslips" element={<AppUkPayslip />} />
            <Route path="/app/tax-forms" element={<AppTaxForms />} />
            <Route path="/app/legal-forms" element={<AppLegalForms />} />
            <Route path="/app/business-forms" element={<AppBusinessForms />} />
            <Route path="/app/resumes" element={<AppResumes />} />
            <Route path="/app/c/:slug" element={<AppCustomDocs />} />
            <Route path="/app/terms" element={<AppTerms />} />
            <Route path="/app/privacy" element={<AppPrivacy />} />
            <Route path="/app/settings" element={<AppSettings />} />
            <Route path="/app/account" element={<AppAccount />} />
              <Route path="/app/my-documents" element={<AppMyDocuments />} />
              <Route path="/app/billing" element={<AppBilling />} />
              <Route path="/app/referrals" element={<AppReferrals />} />
            <Route path="/app/preferences" element={<AppPreferences />} />
            <Route path="/app/business" element={<AppBusiness />} />
            <Route path="/app/employees" element={<AppEmployees />} />
            <Route path="/app/signatures" element={<AppSignatures />} />
            <Route path="/app/signatures/documents/:id" element={<AppSignDocEditor />} />
            <Route path="/app/templates" element={<AppTemplates />} />
            <Route path="/app/templates/edit/:id" element={<AdminTemplateEditor />} />
            <Route path="/app/branding" element={<Navigate to="/app/preferences?segment=branding" replace />} />
              <Route path="/app/minty" element={<Navigate to="/app" replace />} />
            <Route path="/mintslip-vs-other-paystub-generators" element={<ComparisonPage />} />
            <Route path="/mintslip-vs-thepaystubs" element={<MintSlipVsThePayStubs />} />
            <Route path="/generators" element={<DocumentDirectory />} />
            <Route path="*" element={<NotFound />} />
          </DismissRoutes></Suspense>
                </MinimizedChatsProvider>
        </BrowserRouter>
      </div>
    </IPBanCheck>
    </MaintenanceCheck>
    </Elements>
  </HelmetProvider>
  );
}

export default App;